import React, { useContext, useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import { useAchievement } from '../../contexts/AchievementContext';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import useAchievementTriggers from '../../hooks/useAchievementTriggers';
import axios from '../../utils/axiosConfig';
import BouncingImage from '../BouncingImage';
import { isQuestStale } from '../../utils/questPlan';
import QuestBanner from '../Quests/QuestBanner';
import QuestRenewalBanner from '../Quests/QuestRenewalBanner';
import TutorialRestartButton from '../TutorialRestartButton';
import kittyExcite from './Badges/kitty_excite.png';
import Badge from './components/Badge';
import TutorialTooltip from './components/TutorialTooltip';
import { TUTORIAL_STEPS } from './config/tutorialConfig';
import { UserInfo } from './DailyDigestComponents';
import OriginalProgressStats from './OriginalProgressStats';
import QuestMiddleColumn from './QuestMiddleColumn';
import TimelineProgressStats from './TimelineProgressStats';
import TodaysGoalsColumn from './TodaysGoalsColumn';

import { getTodayCalendarDate, getUserTimezone } from '../../utils/timezoneUtils';

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center h-full">
    <div className="mb-4">
      <BouncingImage imageLink={kittyExcite} width={5} />
    </div>
    <p className="text-gray-500 text-sm">Loading your quest...</p>
  </div>
);

const getTodaysTasks = (allTasks) => {
  // Since tasks are now calendar-date based, just return all active tasks
  // The backend handles filtering by calendar date
  return allTasks;
};

const DailyDigest = () => {
  const [tasks, setTasks] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [lastTaskId, setLastTaskId] = useState(false);
  const [monthSize, setMonthSize] = useState(31);
  const [activeDays, setActiveDays] = useState([]);
  const [userID] = useState(localStorage.getItem('userID'));
  const [showTutorial, setShowTutorial] = useState(false);
  const {
    userProfile,
    trialStatus,
    updateTutorialFlag,
    completedNodes,
    nodes,
    questions,
    userSchool,
    questPlan,
    isQuestLoading,
  } = useContext(GlobalContext);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingTasks, setIsAddingTasks] = useState(false);
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);
  const { currentApps } = useContext(ApplicationTrackerContext);
  const navigate = useNavigate();
  const sectionRefs = {
    goals: useRef(null),
    achievements: useRef(null),
    calendar: useRef(null),
    events: useRef(null),
  };
  const [viewMode, setViewMode] = useState('original'); // 'original' or 'timeline'
  const {
    achievements,
    loading: achievementsLoading,
    error: achievementsError,
    fetchAchievements,
  } = useAchievement();
  const checkAchievements = useAchievementTriggers();
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // If the user has not seen the accept question modal, show it
  // useEffect(() => {
  //     if (userProfile) {
  //         setTimeout(() => {
  //             if (!userProfile.notifications_seen.includes('accept_question') && !questPlan && questPlan.quest_plan.length === 0) {
  //                 navigate('/accept-quest');
  //             }
  //         }, 1000);
  //     }
  // }, [userProfile]);

  useEffect(() => {
    if (questPlan) {
      if (questPlan.quest_plan && questPlan.quest_plan.length > 0) {
        setViewMode('timeline');
      }
    } else {
      setViewMode('original'); // or whatever default view mode you want
    }
  }, [questPlan]);

  // Calculate learning tree progress statistics
  const getLearningTreeStats = () => {
    if (!nodes || !completedNodes) {
      return {
        totalNodes: 0,
        completedCount: 0,
        percentComplete: 0,
        corePercentComplete: 0,
        advancedPercentComplete: 0,
        nextNodes: [],
        coreNodes: [],
        completedCoreNodes: [],
        advancedNodes: [],
        completedAdvancedNodes: [],
      };
    }

    const totalNodes = Object.keys(nodes).length;
    const completedCount = completedNodes.length;
    const percentComplete = totalNodes > 0 ? Math.round((completedCount / totalNodes) * 100) : 0;

    // Count core nodes (those without 'variant' or variant !== 'yellow')
    const coreNodes = Object.values(nodes).filter(
      (node) => !node.variant || node.variant !== 'yellow'
    );

    // Count completed core nodes
    const completedCoreNodes = completedNodes.filter(
      (nodeId) => !nodes[nodeId]?.variant || nodes[nodeId]?.variant !== 'yellow'
    );

    // Calculate core completion percentage
    const corePercentComplete =
      coreNodes.length > 0 ? Math.round((completedCoreNodes.length / coreNodes.length) * 100) : 0;

    // Count advanced nodes (those with variant === 'yellow')
    const advancedNodes = Object.values(nodes).filter((node) => node.variant === 'yellow');

    // Count completed advanced nodes
    const completedAdvancedNodes = completedNodes.filter(
      (nodeId) => nodes[nodeId]?.variant === 'yellow'
    );

    // Calculate advanced completion percentage
    const advancedPercentComplete =
      advancedNodes.length > 0
        ? Math.round((completedAdvancedNodes.length / advancedNodes.length) * 100)
        : 0;

    // Find nodes that are unlocked but not completed
    const nextNodes = [];

    // Helper function to check if a node is available (at least one parent is completed)
    const isNodeAvailable = (nodeId) => {
      if (nodeId === 'start') return true;

      // Find parents of this node
      const parents = Object.values(nodes)
        .filter((node) => node.children && node.children.includes(nodeId))
        .map((node) => node.id);

      // Node is available if at least one parent is completed
      return parents.some((parentId) => completedNodes.includes(parentId));
    };

    // Find unlocked but incomplete nodes
    Object.values(nodes).forEach((node) => {
      if (!completedNodes.includes(node.id) && isNodeAvailable(node.id)) {
        nextNodes.push(node);
      }
    });

    return {
      totalNodes,
      completedCount,
      percentComplete,
      corePercentComplete,
      advancedPercentComplete,
      nextNodes,
      coreNodes,
      completedCoreNodes,
      advancedNodes,
      completedAdvancedNodes,
    };
  };

  // Get learning stats
  const [learningStats, setLearningStats] = useState(null);
  useEffect(() => {
    const learningStats = getLearningTreeStats();
    setLearningStats(learningStats);
  }, [nodes, completedNodes]);

  // Calculate application statistics based on current applications
  const getApplicationStats = () => {
    if (!currentApps || currentApps.length === 0) {
      return {
        total: 0,
        interviews: 0,
        offers: 0,
        thisWeek: 0,
      };
    }

    // Filter out applications with "Application Desired" or "Application Started" status
    const submittedApps = currentApps.filter(
      (app) => app.status !== 'Application Desired' && app.status !== 'Application Started'
    );

    // Calculate start of current week (Sunday)
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Go back to Sunday
    startOfWeek.setHours(0, 0, 0, 0); // Set to beginning of day

    // Find applications submitted this week, which is all applications except app desired and app started
    const appsThisWeek = submittedApps.filter((app) => {
      if (!app.date) return false;
      const appDate = new Date(app.date);
      return appDate >= startOfWeek;
    });

    const total = submittedApps.length;
    const thisWeek = appsThisWeek.length;
    const interviews = currentApps.filter(
      (app) => app.status === 'Live Interviewing' || app.status === 'Online Assessment'
    ).length;
    const offers = currentApps.filter((app) => app.status === 'Offer Received').length;

    return { total, interviews, offers, thisWeek };
  };

  const [appStats, setAppStats] = useState(null);
  useEffect(() => {
    const appStats = getApplicationStats();
    setAppStats(appStats);
  }, [currentApps]);

  // Calculate technical interview statistics
  const getTechnicalStats = () => {
    if (!questions || questions.length === 0) {
      return {
        solved: 0,
        total: 0,
        percentComplete: 0,
        easy: { solved: 0, total: 0, percent: 0 },
        medium: { solved: 0, total: 0, percent: 0 },
        hard: { solved: 0, total: 0, percent: 0 },
        attempted: 0,
        nextProblem: null,
      };
    }

    // Get totals
    const total = questions.length;
    const solved = questions.filter((q) => q.status === 'solved').length;
    const attempted = questions.filter((q) => q.status === 'attempted').length;
    const percentComplete = total > 0 ? Math.round((solved / total) * 100) : 0;

    // Group by difficulty
    const easyQuestions = questions.filter((q) => q.difficulty?.toLowerCase() === 'easy');
    const mediumQuestions = questions.filter((q) => q.difficulty?.toLowerCase() === 'medium');
    const hardQuestions = questions.filter((q) => q.difficulty?.toLowerCase() === 'hard');

    const easyStats = {
      solved: easyQuestions.filter((q) => q.status === 'solved').length,
      total: easyQuestions.length,
      percent:
        easyQuestions.length > 0
          ? Math.round(
              (easyQuestions.filter((q) => q.status === 'solved').length / easyQuestions.length) *
                100
            )
          : 0,
    };

    const mediumStats = {
      solved: mediumQuestions.filter((q) => q.status === 'solved').length,
      total: mediumQuestions.length,
      percent:
        mediumQuestions.length > 0
          ? Math.round(
              (mediumQuestions.filter((q) => q.status === 'solved').length /
                mediumQuestions.length) *
                100
            )
          : 0,
    };

    const hardStats = {
      solved: hardQuestions.filter((q) => q.status === 'solved').length,
      total: hardQuestions.length,
      percent:
        hardQuestions.length > 0
          ? Math.round(
              (hardQuestions.filter((q) => q.status === 'solved').length / hardQuestions.length) *
                100
            )
          : 0,
    };

    // Find next problem to solve (prioritizing Easy, then Medium, then Hard)
    let nextProblem =
      easyQuestions.find((q) => q.status === 'unsolved') ||
      mediumQuestions.find((q) => q.status === 'unsolved') ||
      hardQuestions.find((q) => q.status === 'unsolved') ||
      null;

    return {
      solved,
      total,
      percentComplete,
      attempted,
      easy: easyStats,
      medium: mediumStats,
      hard: hardStats,
      nextProblem,
    };
  };

  const [techStats, setTechStats] = useState(null);

  useEffect(() => {
    const techStats = getTechnicalStats();
    setTechStats(techStats);
  }, [questions]);

  useEffect(() => {
    if (userProfile) {
      setShowTutorial(userProfile.onboarding_progress.hasSeenDailyDigestTutorial !== true);
    }
  }, [userProfile]);
  // Fetch user tasks - now uses generated tasks API
  const fetchUserTasks = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingTasks) {
      console.log('Already fetching tasks, skipping duplicate request');
      return;
    }

    setIsFetchingTasks(true);
    try {
      // Use the unified mixed-tasks endpoint (fully migrated to new system)
      const response = await axios.get(`/api/v1/user/${userID}/mixed-tasks`);
      const tasksData = response.data;

      // Deduplicate tasks by task_id to prevent duplicates
      const uniqueTasks = [];
      const seenTaskIds = new Set();

      for (const task of tasksData) {
        if (!seenTaskIds.has(task.task_id)) {
          seenTaskIds.add(task.task_id);
          uniqueTasks.push(task);
        }
      }

      // Backend now properly handles task generation, so no retry needed
      // Sort tasks by ascending task_id
      const sortedTasks = uniqueTasks.sort((a, b) => a.task_id - b.task_id);
      setTasks(sortedTasks);
      setIsComplete(sortedTasks.every((task) => task.completed));
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setIsLoading(false);
    } finally {
      setIsFetchingTasks(false);
    }
  };

  useEffect(() => {
    if (userID) {
      fetchUserTasks();
      fetchAchievements();
    }
  }, [userID]);

  // Helper function to get display text for trial status
  const getTrialStatusDisplay = () => {
    if (!trialStatus) return 'Loading...';

    if (!trialStatus.has_trial) return 'No Trial';

    // Determine account type display
    let accountType = 'Trial';
    if (trialStatus.trial_type === 'basic') {
      accountType = 'Basic Trial';
    } else if (trialStatus.is_promoted) {
      accountType = 'Permanent Account';
    }

    // Add expiration info for active trials
    if (
      !trialStatus.is_expired &&
      !trialStatus.is_revoked &&
      !trialStatus.is_promoted &&
      trialStatus.days_remaining !== undefined
    ) {
      return `${accountType} (${trialStatus.days_remaining} days left)`;
    } else if (trialStatus.is_expired) {
      return `${accountType} (Expired)`;
    } else if (trialStatus.is_revoked) {
      return `${accountType} (Revoked)`;
    } else if (trialStatus.is_promoted) {
      return 'Permanent Account';
    }

    return accountType;
  };

  const handleTaskToggle = async (taskId) => {
    const taskToUpdate = tasks.find((task) => task.task_id === taskId);
    if (!taskToUpdate) return;

    try {
      // Get user's timezone for calendar date calculation
      const userTimezone = getUserTimezone();

      // Always use the new generated tasks API - unified system
      await axios.put(
        `/api/v1/user/${userID}/generated-task/${taskId}`,
        { completed: !taskToUpdate.completed },
        {
          headers: {
            'X-User-Timezone': userTimezone,
          },
        }
      );

      // Update the task state locally instead of refetching all tasks
      const updatedTasks = tasks.map((task) =>
        task.task_id === taskId
          ? {
              ...task,
              completed: !task.completed,
              completed_at: !task.completed ? new Date().toISOString() : null,
            }
          : task
      );
      setTasks(updatedTasks);

      // Only check and show modal if we just completed a task (not when unchecking)
      if (!taskToUpdate.completed) {
        const todaysTasks = getTodaysTasks(updatedTasks);
        const allTodaysTasksCompleted =
          todaysTasks.length > 0 && todaysTasks.every((task) => task.completed);

        if (allTodaysTasksCompleted && !isComplete) {
          setLastTaskId(taskId);
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleConfirmCompletion = async () => {
    setIsComplete(true);
    setShowModal(false);
    setConfetti(true);
    // Trigger the Task Master achievement
    checkAchievements.checkDailyTasks();

    const todayDate = getTodayCalendarDate();

    try {
      // Get user's timezone for calendar date calculation
      const userTimezone = getUserTimezone();

      await axios.post(
        `/api/v1/user/day/${userID}`,
        {
          day: todayDate,
          timezone: userTimezone,
        },
        {
          headers: {
            'X-User-Timezone': userTimezone,
          },
        }
      );
      fetchActiveDays();
    } catch (error) {
      console.error('Error updating user login days:', error);
    }
    setTimeout(() => setConfetti(false), 5000); // Confetti lasts 5s
  };

  const handleNotYet = async () => {
    setShowModal(false);
    if (lastTaskId) {
      try {
        // Get user's timezone for calendar date calculation
        const userTimezone = getUserTimezone();

        // Always use the new generated tasks API - unified system
        await axios.put(
          `/api/v1/user/${userID}/generated-task/${lastTaskId}`,
          { completed: false },
          {
            headers: {
              'X-User-Timezone': userTimezone,
            },
          }
        );

        // Update local state
        const updatedTasks = tasks.map((task) =>
          task.task_id === lastTaskId ? { ...task, completed: false, completed_at: null } : task
        );
        setTasks(updatedTasks);
      } catch (error) {
        console.error('Error uncompleting task:', error);
      }
    }
  };

  const fetchActiveDays = async () => {
    try {
      const response = await axios.get(`/api/v1/user/active-days/${userID}`);
      setActiveDays(response.data.activeDays || []);
    } catch (error) {
      console.error('Error fetching active days:', error);
    }
  };

  useEffect(() => {
    fetchActiveDays();
  }, [userID]);

  useEffect(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    setMonthSize(daysInMonth);
  }, []);

  const handleNextStep = () => {
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenDailyDigestTutorial', true);
    // Trigger the welcome whiskers achievement
    checkAchievements.checkFirstVisit();
  };

  // Add effect to handle body scroll
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  // Add this effect to refresh achievements when the component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !achievementsLoading) {
        // Force a re-fetch of achievements when tab becomes visible
        checkAchievements.refreshAchievements();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [achievementsLoading, checkAchievements]);

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = sectionRefs[currentStep.id];
    const rect = currentRef.current?.getBoundingClientRect();

    if (!rect) return null;

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (currentStep.id === 'goals' || currentStep.id === 'events') {
        return {
          top: rect.top + rect.height / 2 - 200,
          left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
          width: '300px',
          '--arrow-orientation': 'right', // Custom CSS variable for arrow direction
        };
      }

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
        };
      }

      return {
        top: rect.top + rect.height / 2 - 200,
        left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
        width: '300px',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  // Function to force refresh tasks (only when needed, like after lesson completion)
  const refreshTasksIfNeeded = async () => {
    // Only refresh if not already fetching
    if (!isFetchingTasks) {
      await fetchUserTasks();
    }
  };

  // Update isComplete state whenever tasks change
  useEffect(() => {
    const todaysTasks = getTodaysTasks(tasks);
    setIsComplete(todaysTasks.length > 0 && todaysTasks.every((task) => task.completed));
  }, [tasks]);

  // Function to add new tasks after lesson completion or other events
  const addNewTasksAfterEvent = async () => {
    // This should only be called when we know new tasks should be generated
    // Only refresh if not already fetching
    if (!isFetchingTasks) {
      await fetchUserTasks();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="container mx-auto p-4 space-y-6">
      {!questPlan &&
        userProfile &&
        !userProfile.notifications_seen.includes('quest_banner') &&
        userProfile.onboarding_progress?.hasSeenDailyDigestTutorial === true && <QuestBanner />}
      {/* Users who already accepted a past cycle's quest never see QuestBanner
          above, since it is gated on having no quest at all. They get a
          renewal offer instead. */}
      {questPlan && isQuestStale(questPlan) && <QuestRenewalBanner />}
      {/* <button
                onClick={() => setViewMode('original')}
                className={`pax-3 py-1 rounded-md text-sm font-medium transition-colors ${viewMode === 'original'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
            >
                Stats View
            </button>
            <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${viewMode === 'timeline'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
            >
                Timeline View
            </button> */}
      {confetti && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
          />
        </div>
      )}
      {showTutorial && <TutorialOverlay />}
      {viewMode === 'original' && (
        <TutorialRestartButton tutorialKey="hasSeenDailyDigestTutorial" />
      )}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">🎉 Congratulations!</h2>
            <p className="text-gray-600 text-lg mb-6">You've completed all tasks for today!</p>
            <div className="space-y-3">
              <button
                onClick={handleConfirmCompletion}
                className="w-full px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors duration-200"
              >
                Yes, celebrate! 🎊
              </button>
              <button
                onClick={handleNotYet}
                className="w-full px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200"
              >
                Not yet
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 min-h-[600px]">
        {/* Column 1: Account Info and Achievements - Now 3/12 width */}
        <div className="md:col-span-3 flex flex-col space-y-6 h-full">
          <div className="flex flex-col items-center justify-center bg-white p-6 rounded-lg shadow-md shrink-0">
            <UserInfo
              name={
                userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : 'Loading...'
              }
              school={
                userProfile?.account_type === 'trial' && trialStatus
                  ? getTrialStatusDisplay()
                  : userSchool || 'Loading...'
              }
              joinDate={
                userProfile ? new Date(userProfile.created_at).toLocaleDateString() : 'Loading...'
              }
              questPlan={questPlan}
              onAcceptQuest={() => navigate('/accept-quest')}
            />
          </div>

          <div
            ref={sectionRefs.achievements}
            className="bg-white p-6 rounded-lg shadow-md overflow-hidden flex-1 flex flex-col"
          >
            <h2 className="text-2xl font-semibold mb-4">Achievements</h2>
            <div className="relative flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto no-scrollbar">
                {achievementsLoading || achievementsError ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 px-2">
                    {achievements
                      .sort((a, b) => a.id - b.id)
                      .map((badge) => (
                        <div key={badge.id} className="flex justify-center py-2">
                          <Badge
                            imageUrl={badge.imageUrl}
                            alt={badge.alt}
                            description={badge.description}
                            locked={badge.locked}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-linear-to-t from-white to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>

        {/* Column 2: Today's Goals and Active Days */}
        {viewMode === 'original' ? (
          <TodaysGoalsColumn
            sectionRefs={sectionRefs}
            getTodaysTasks={getTodaysTasks}
            handleTaskToggle={handleTaskToggle}
            tasks={tasks}
            isLoading={isLoading}
            isAddingTasks={isAddingTasks}
            activeDays={activeDays}
            checkAchievements={checkAchievements}
            userID={userID}
            onRefreshActiveDays={fetchActiveDays}
          />
        ) : (
          <QuestMiddleColumn />
        )}

        {/* Column 3: Progress Stats */}
        <div className="md:col-span-4" ref={sectionRefs.events}>
          {isQuestLoading ? (
            <div className="bg-white p-6 rounded-lg shadow-md h-[calc(100vh-8rem)]">
              <LoadingState />
            </div>
          ) : viewMode === 'original' ? (
            <OriginalProgressStats
              learningStats={learningStats}
              appStats={appStats}
              techStats={techStats}
            />
          ) : (
            <TimelineProgressStats questions={questions} currentApps={currentApps} />
          )}
        </div>
      </section>

      <style jsx>{`
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default DailyDigest;
